import { t } from "@/i18n-js/instance";
import { formatPaywallCurrencyAmount } from "@/react/helpers/paywalls/currencies";
import type { PaywallCurrency } from "@/react/types/Paywall";

const PAGINATION_LIMIT_THRESHOLD = 1000;

export const getPaginationLimitThreshold = (value: string | number): number =>
  Math.min(Number(value) || 1, PAGINATION_LIMIT_THRESHOLD);

export const callTriggerWhenNotSelectingText =
  (trigger: () => void) => (): void => {
    if (window.getSelection()?.toString() === "") {
      trigger();
    }
  };

export const mapCurrencyAmountStrToInt = (
  currencyAmountStr: string | number,
): number =>
  parseInt(String(currencyAmountStr || "0").replace(/[^\d\\-]/g, ""));

export const mapCurrencyAmountStrToDecimal = (
  currencyAmountStr: string | number,
): number =>
  // eslint-disable-next-line sonarjs/duplicates-in-character-class -- Disabled for plugin version update. Fix when possible
  parseFloat(String(currencyAmountStr || "0").replace(/[^\d\\.\\-]/g, ""));

interface TransformInputValueOptions {
  amountInCents?: boolean;
}

export const getMaximumAmount = (
  maximumAmount: number | undefined,
  amount: number,
  amountInCents: boolean,
) => {
  if (!maximumAmount || isNaN(maximumAmount)) {
    return amount;
  }

  let maxValueAllowed = maximumAmount;
  if (!amountInCents) {
    maxValueAllowed = Math.round(maximumAmount / 100);
  }
  return Math.min(amount, maxValueAllowed);
};

export const buildTransformInputValueToCurrencyAmountStrFunc = (
  currency: Pick<
    PaywallCurrency,
    "smallest_unit" | "code" | "symbol" | "direction"
  > &
    Partial<PaywallCurrency>,
  options: TransformInputValueOptions = {},
) => {
  const defaultOpts: Required<TransformInputValueOptions> = {
    amountInCents: true,
  };
  const opts = { ...defaultOpts, ...options };

  return (inputValue: string | number): string | undefined => {
    let amount: number;
    if (opts.amountInCents) {
      amount = mapCurrencyAmountStrToInt(inputValue);
    } else {
      amount = mapCurrencyAmountStrToDecimal(inputValue);
    }
    if (!isNaN(amount)) {
      amount = getMaximumAmount(
        currency.max_charge_amount,
        amount,
        !!opts.amountInCents,
      );

      return formatPaywallCurrencyAmount(currency, amount, {
        amountInCents: opts.amountInCents,
        includeCurrencyCode: false,
        forceMaxFractionDigits: true,
      });
    }
    return undefined;
  };
};

export const formatDisplayAmountString = (
  currency: PaywallCurrency,
  interval: string | null,
  intervalCount: number,
  amount: number,
): string => {
  let formattedAmount = formatPaywallCurrencyAmount(currency, amount);
  if (!interval) {
    return `${formattedAmount} ${t(
      "settings.paywall_prices.onetime_price_text",
    )}`;
  }

  formattedAmount += " / ";
  formattedAmount += t(`settings.paywall_prices.interval_labels.${interval}`, {
    count: intervalCount,
  });
  return formattedAmount;
};

interface QueryParams {
  page?: string | number;
  search_key?: string;
  tab?: string;
}

interface SanitizedQueryParams {
  page: number;
  search_key?: string;
  tab?: string;
}

export const queryParamsSanitizer = (
  { tab, page, search_key }: QueryParams,
  defaultTabs: Record<string, string> = {},
): SanitizedQueryParams => ({
  tab: tab && Object.values(defaultTabs).includes(tab) ? tab : undefined,
  page: isNaN(Number(page)) ? 1 : Number(page),
  search_key,
});
